<template>
  <div class="tx-dealersearch module-google-map" ref="container">
    <div class="map-wrapper" ref="wrapper">
      <div v-if="loadError" class="module-placeholder module-placeholder--vue" role="alert">
        <div class="consent__wrapper">
          <div class="consent__text">{{ $t('dealersearch.map.loadError.text') }}</div>
          <button class="consent__button -btn-placeholder -button btn -btn-alpha" :disabled="mapLoading" @click="initializeMap">
            <span>{{ $t('dealersearch.map.loadError.retry') }}</span>
          </button>
        </div>
      </div>
      <div :id="id" :ref="id">
      </div>
      <ul v-if="map && !loadError" class="map-legend" :aria-label="$t('dealersearch.map.legend.label')">
        <li class="map-legend__item">
          <img class="map-legend__icon" :src="markerIconPoi.url" alt="">
          <span>{{ $t('dealersearch.map.legend.showroom') }}</span>
        </li>
        <li class="map-legend__item">
          <img class="map-legend__icon" :src="markerIconPoiFair.url" alt="">
          <span>{{ $t('dealersearch.map.legend.fair') }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/* global google */
import { mapState } from 'vuex'
import { createDealerPopup } from '@/utils/dealer-popup'

export default {
  name: 'GoogleMap',
  inject: ['apiBase', 'detailListingBase', 'googleMapsApiUrl', 'mobile', 'phone'],
  data () {
    return {
      map: null,
      mapBounds: {},
      markerIconPoi: {
        url: ''
      },
      markerIconPoiActive: {
        url: ''
      },
      markerIconPoiFair: {
        url: ''
      },
      markerIconPoiFairActive: {
        url: ''
      },
      markerClickPopups: [],
      markerIsFair: [],
      lastClickedIndex: -1,
      defaultDealerPicURL: '',
      mapClickListener: null,
      mapLoading: false,
      loadError: false
    }
  },
  props: {
    id: {
      type: String,
      default: 'map'
    },
    hoveredPoiIndex: {
      type: Number,
      default: -1
    },
    clickedPoiIndex: {
      type: Number,
      default: -1
    }
  },
  computed: {
    ...mapState({
      mandant: state => state.mandant,
      moreLoaded: state => state.moreLoaded,
      pois (state) {
        const hasFilters =
          state.attribute.filterCategories.length > 0 ||
          !!state.latitude || !!state.longitude

        const initialAllMode =
          state.dealer.loadedAllOnce &&
          state.dealer.currentPage === 1 &&
          !hasFilters

        return initialAllMode
          ? state.dealer.dealersAll
          : state.dealer.dealers
      },
      latitudeSearched: state => state.latitude,
      longitudeSearched: state => state.longitude
    })
  },
  watch: {
    pois: function (newPois) {
      const self = this
      self.$emit('closedPoiIndex')
      if (!self.map) return

      if (self.markers) {
        for (let i = 0; i < self.markers.length; i++) {
          self.markers[i].setMap(null)
        }
      }
      self.markers = []
      self.markerIsFair = []
      self.mapBounds = new google.maps.LatLngBounds()

      if (newPois.length) {
        for (let i = 0; i < newPois.length; i++) {
          self.addMapMarker(self, newPois[i], i)
        }
        if (!self.bounds) {
          self.map.fitBounds(self.mapBounds)
        }
      } else {
        if (self.latitudeSearched && self.longitudeSearched) {
          self.map.setCenter({ lat: self.latitudeSearched, lng: self.longitudeSearched })
        } else {
          self.map.setCenter({ lat: 48.0532510192798, lng: 12.770071628931637 })
        }
        self.map.setZoom(13)
      }
    },
    hoveredPoiIndex: function (newHoveredPoi, oldHoveredPoi) {
      const self = this
      if (!self.map || !self.markers) return
      if (oldHoveredPoi >= 0 && self.markers[oldHoveredPoi]) {
        self.markers[oldHoveredPoi].setIcon(self.markerIcon(oldHoveredPoi, false))
        if (self.markerClickPopups[oldHoveredPoi]?.setMap) self.markerClickPopups[oldHoveredPoi].setMap(null)
        self.$emit('closedPoiIndex')
      }
      if (newHoveredPoi >= 0 && self.markers[newHoveredPoi]) {
        self.markers[newHoveredPoi].setIcon(self.markerIcon(newHoveredPoi, true))
        self.openClickPopup(self, self.pois[newHoveredPoi], newHoveredPoi)
      }
    },
    clickedPoiIndex: function (newClickedPoi, oldClickedPoi) {
      const self = this
      if (!self.map || !self.markers) return
      if (oldClickedPoi >= 0 && self.markers[oldClickedPoi]) {
        self.markers[oldClickedPoi].setIcon(self.markerIcon(oldClickedPoi, false))
      }
      if (newClickedPoi >= 0 && self.markers[newClickedPoi]) {
        self.markers[newClickedPoi].setIcon(self.markerIcon(newClickedPoi, true))
      }
    }
  },
  mounted () {
    this.initializeMap()
  },
  beforeUnmount () {
    if (this.mapClickListener && window.google && google.maps && google.maps.event) {
      google.maps.event.removeListener(this.mapClickListener)
    }
    if (this.map && window.google && google.maps && google.maps.event) {
      google.maps.event.clearInstanceListeners(this.map)
    }
    if (this.markers) {
      this.markers.forEach(marker => {
        if (window.google && google.maps && google.maps.event) {
          google.maps.event.clearInstanceListeners(marker)
        }
        marker.setMap(null)
      })
    }
    this.markerClickPopups.forEach(popup => {
      if (popup?.setMap) popup.setMap(null)
      if (popup?.el?.parentElement) popup.el.parentElement.removeChild(popup.el)
    })
    this.mapClickListener = null
    this.map = null
  },
  methods: {
    async initializeMap () {
      if (this.map || this.mapLoading) return
      if (!window.armstarkGoogleMaps || typeof window.armstarkGoogleMaps.load !== 'function') {
        this.loadError = true
        return
      }

      this.mapLoading = true
      this.loadError = false
      try {
        await window.armstarkGoogleMaps.load(this.googleMapsApiUrl)
        if (!this.$refs[this.id]) return

        const markerDimensions = {
          anchor: new google.maps.Point(14, 43),
          scaledSize: new google.maps.Size(28, 43)
        }
        Object.assign(this.markerIconPoi, markerDimensions)
        Object.assign(this.markerIconPoiActive, markerDimensions)
        Object.assign(this.markerIconPoiFair, markerDimensions)
        Object.assign(this.markerIconPoiFairActive, markerDimensions)

        this.markerIconPoi.url = require('@/assets/icon-map-marker.png')
        this.markerIconPoiFair.url = require('@/assets/icon-map-marker-fair.png')
        this.markerIconPoiActive.url = require('@/assets/icon-map-marker-active.png')
        // Messen behalten beim Hervorheben ihr graues Symbol, damit sie nicht zur Ausstellung werden
        this.markerIconPoiFairActive.url = require('@/assets/icon-map-marker-fair.png')
        this.defaultDealerPicURL = require('@/assets/dealer-search-image-default.jpg')

        const center = { lat: 48.0532510192798, lng: 12.770071628931637 }
        if (this.latitudeSearched && this.longitudeSearched) {
          center.lat = parseFloat(this.latitudeSearched)
          center.lng = parseFloat(this.longitudeSearched)
        }

        this.map = new google.maps.Map(this.$refs[this.id], {
          center: center,
          scrollwheel: !this.mobile,
          zoom: 13,
          minZoom: null,
          maxZoom: 18,
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          styles: [
            {
              featureType: 'poi',
              stylers: [{ visibility: 'off' }]
            }
          ]
        })

        this.mapBounds = new google.maps.LatLngBounds()
        if (this.pois.length) {
          this.markers = []
          for (let i = 0; i < this.pois.length; i++) {
            this.addMapMarker(this, this.pois[i], i)
          }
          if (!this.bounds) {
            this.map.fitBounds(this.mapBounds)
          }
        }

        this.mapClickListener = this.map.addListener('click', () => {
          const i = this.lastClickedIndex
          if (i >= 0) {
            const prev = this.markerClickPopups[i]
            if (prev?.mobile) {
              if (prev.el?.parentElement) prev.el.parentElement.removeChild(prev.el)
            } else if (prev?.setMap) {
              prev.setMap(null)
            }
            this.$emit('closedPoiIndex')
            this.lastClickedIndex = -1
          }
        })
      } catch (error) {
        this.map = null
        this.loadError = true
        if (this.$refs[this.id]) this.$refs[this.id].innerHTML = ''
      } finally {
        this.mapLoading = false
      }
    },
    createPopupClass: function () {
      function Popup (position, content) {
        this.position = position
        this.content = content

        // Optionally stop clicks, etc., from bubbling up to the map.
        google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.content)
      }

      // ES5 magic to extend google.maps.OverlayView.
      Popup.prototype = Object.create(google.maps.OverlayView.prototype)

      /** Called when the popup is added to the map. */
      Popup.prototype.onAdd = function () {
        this.getPanes().floatPane.appendChild(this.content)
      }

      /** Called when the popup is removed from the map. */
      Popup.prototype.onRemove = function () {
        if (this.content.parentElement) {
          this.content.parentElement.removeChild(this.content)
        }
      }

      /** Called each frame when the popup needs to draw itself. */
      Popup.prototype.draw = function () {
        const projection = this.getProjection()
        const divPosition = projection.fromLatLngToDivPixel(this.position)

        const display =
          Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
            ? 'block'
            : 'none'

        if (display === 'block') {
          const w = this.content.offsetWidth
          const h = this.content.offsetHeight

          const x = divPosition.x - w / 2
          const y = divPosition.y - h

          this.content.style.position = 'absolute'
          this.content.style.left = `${x}px`
          this.content.style.top = `${y}px`
        }

        if (this.content.style.display !== display) {
          this.content.style.display = display
        }
      }

      return Popup
    },
    markerIcon: function (index, active) {
      if (this.markerIsFair[index]) {
        return active ? this.markerIconPoiFairActive : this.markerIconPoiFair
      }
      return active ? this.markerIconPoiActive : this.markerIconPoi
    },
    addMapMarker: function (self, poi, index) {
      const LatLng = new google.maps.LatLng(poi.lat, poi.lng)

      self.markerIsFair[index] = poi.attributes.some(attr => attr.title && attr.title.toLowerCase().includes('messe'))

      self.markers[index] = new google.maps.Marker({
        position: LatLng,
        title: poi.name,
        icon: self.markerIcon(index, false)
      })

      self.markers[index].addListener('click', function () {
        self.$emit('clickedPoiIndex', self.pois.indexOf(poi))
        self.openClickPopup(self, poi, index)
      })

      if (!this.bounds) {
        self.mapBounds.extend(LatLng)
      }
      self.markers[index].setMap(self.map)
    },
    openClickPopup: function (self, poi, index) {
      const LatLng = new google.maps.LatLng(poi.lat, poi.lng)

      const module = createDealerPopup({
        dealer: poi,
        detailListingBase: self.detailListingBase,
        fallbackImageUrl: self.defaultDealerPicURL,
        labels: {
          detail: self.$i18n.t('dealersearch.map.popup.button'),
          phone: self.$i18n.t('dealersearch.map.popup.phone'),
          route: self.$i18n.t('dealersearch.map.popup.route')
        },
        mobile: self.mobile
      })

      self.map.addListener('click', function () {
        if (self.lastClickedIndex >= 0) {
          self.markerClickPopups[self.lastClickedIndex].setMap(null)
          self.$emit('closedPoiIndex')
        }
      })

      if (self.lastClickedIndex >= 0) {
        const prev = self.markerClickPopups[self.lastClickedIndex]
        if (prev?.mobile) {
          if (prev.el?.parentElement) prev.el.parentElement.removeChild(prev.el)
        } else if (prev?.setMap) {
          prev.setMap(null)
        }
        self.$emit('closedPoiIndex')
      }

      if (self.markerClickPopups[index]) {
        const prev = self.markerClickPopups[index]
        if (prev?.mobile) {
          if (prev.el?.parentElement) prev.el.parentElement.removeChild(prev.el)
        } else if (prev?.setMap) {
          prev.setMap(null)
        }
        self.$emit('closedPoiIndex')
      }

      if (self.mobile) {
        const host = self.$refs.wrapper || self.$refs[self.id].parentElement
        host.appendChild(module)

        self.markerClickPopups[index] = { mobile: true, el: module }
        self.$emit('clickedPoiIndex', index)
        self.lastClickedIndex = index
        self.skipListeners = false
        return
      }

      const Popup = self.createPopupClass()
      self.markerClickPopups[index] = new Popup(LatLng, module)

      self.markerClickPopups[index].setMap(self.map)
      self.$emit('clickedPoiIndex', index)
      self.lastClickedIndex = index
      self.skipListeners = false
    }
  }
}
</script>
